import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { IMAGES, HERO } from '../lib/site';

const ease = [0.22, 1, 0.36, 1];

const EYEBROW_ANIM = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease } };
const HEADLINE_ANIM = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, ease, delay: 0.1 } };
const SUB_ANIM = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, ease, delay: 0.22 } };
const CTA_ANIM = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, ease, delay: 0.35 } };
const SCROLL_ANIM = { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 1.2, duration: 0.8 } };
const CHEVRON_ANIM = { animate: { y: [0, 5, 0] }, transition: { duration: 1.8, repeat: Infinity, ease: 'easeInOut' } };

const HeroBackground = () => (
  <>
    <div className="absolute inset-0">
      <img
        src={IMAGES.heroBg}
        alt="Aerial view of a cargo vessel navigating open ocean"
        className="w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/70 via-[#050B14]/50 to-[#050B14]/95" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050B14]/80 via-transparent to-[#050B14]/20" />
    </div>
    <div className="absolute inset-0 dot-grid opacity-20 [mask-image:linear-gradient(to_bottom,transparent,black_30%,black_70%,transparent)]" />
  </>
);

const HeroCtas = () => (
  <motion.div {...CTA_ANIM} className="mt-12 flex flex-wrap items-center gap-4">
    <a
      href="#contact"
      data-testid="hero-primary-cta"
      className="group inline-flex items-center gap-2 font-body font-semibold text-[13px] text-[#050B14] bg-[#00E5FF] hover:bg-white transition-colors duration-300 px-8 py-4 uppercase tracking-widest"
    >
      {HERO.cta_primary}
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
    </a>
    <a
      href="#products"
      data-testid="hero-secondary-cta"
      className="inline-flex items-center gap-2 font-body font-medium text-[13px] text-white border border-white/25 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors duration-300 px-8 py-4 uppercase tracking-widest"
    >
      {HERO.cta_secondary}
    </a>
  </motion.div>
);

const ScrollIndicator = () => (
  <motion.div
    {...SCROLL_ANIM}
    className="mt-10 lg:mt-20 flex items-center gap-3 text-[#94A3B8]"
    data-testid="hero-scroll-indicator"
  >
    <span className="h-px w-10 bg-[#94A3B8]/40" />
    <span className="font-body text-[11px] uppercase tracking-[0.2em]">Scroll to explore</span>
    <motion.div {...CHEVRON_ANIM}>
      <ChevronDown size={14} />
    </motion.div>
  </motion.div>
);

export default function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <HeroBackground />

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 pb-16 md:pb-24 pt-32 lg:pt-44 w-full">
        <motion.span
          {...EYEBROW_ANIM}
          className="inline-flex items-center gap-2.5 font-body text-[11px] font-semibold uppercase tracking-[0.25em] text-[#00E5FF] mb-8"
        >
          <span className="h-px w-8 bg-[#00E5FF]" />
          {HERO.eyebrow}
        </motion.span>

        <motion.h1
          {...HEADLINE_ANIM}
          className="font-display font-semibold text-white leading-[1.05] tracking-tight whitespace-pre-line"
          style={{ fontSize: 'clamp(2.8rem, 6vw, 5.5rem)' }}
          data-testid="hero-headline"
        >
          {HERO.headline}
        </motion.h1>

        <motion.p
          {...SUB_ANIM}
          className="mt-8 max-w-xl font-body text-base md:text-lg text-[#94A3B8] leading-relaxed font-light"
          data-testid="hero-subtitle"
        >
          {HERO.sub}
        </motion.p>

        <HeroCtas />
        <ScrollIndicator />
      </div>
    </section>
  );
}
